import { rwtxDarkLogo, rwtxLightLogo } from "../assets";
import { Box, IconButton, Typography } from "@mui/material";
import { styledComponent } from "../utils/styled";
import { TogglePill, ToggleOption } from "./ui";
import {
  FolderOpen,
  LogOut,
  Settings,
  Moon,
  Sun,
  PanelLeftClose,
  PanelLeftOpen,
  ClipboardList,
} from "lucide-mui";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { SubmissionsIcon } from "./icons";

// ── Styled components ────────────────────────────────────────────────────────

const SidebarWrapper = styledComponent(Box)({
  position: "relative",
  flexShrink: 0,
});

const SidebarRoot = styledComponent(Box)(({ isDark, open }) => ({
  width: open ? "197px" : "0px",
  overflow: "hidden",
  transition: "width 0.5s ease",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: isDark
    ? "linear-gradient(180deg, rgba(37,40,42,0.98) 0%, rgba(30,32,34,0.98) 50%, rgba(25,26,27,0.98) 100%)"
    : "linear-gradient(180deg, #FFFFFF 21.58%, #F7FAFF 77.88%, #F7F9FB 95.19%)",
  padding: open ? "0 6px" : "0",
  borderRight: isDark ? "0px" : "1px solid #D5D9DF33",
}));

const Header = styledComponent(Box)({
  padding: "0 12px",
  marginTop: "30px",
  marginBottom: "13px",
  height: "22px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const LogoImg = styledComponent("img")({
  height: "100%",
  cursor: "pointer",
});

const CloseBtn = styledComponent(IconButton)(({ isDark }) => ({
  width: "24px",
  height: "24px",
  color: isDark ? "#D5D9DF" : "#6A7079",
  "&:hover": {
    color: isDark ? "#D5D9DF" : "#3D424A",
    backgroundColor: "transparent",
  },
  "& svg": { width: "24px", height: "24px" },
}));

const OpenBtn = styledComponent(IconButton)(({ isDark, open }) => ({
  position: "absolute",
  top: "30px",
  left: "30px",
  zIndex: 10,
  width: "24px",
  height: "24px",
  color: isDark ? "#D5D9DF" : "#434950",
  opacity: open ? 0 : 1,
  pointerEvents: open ? "none" : "auto",
  transition: open ? "opacity 0.1s ease" : "opacity 0.15s ease 0.4s",
  "&:hover": { backgroundColor: "transparent" },
  "& svg": { width: "24px", height: "24px" },
}));

const NavArea = styledComponent(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: "0 4px",
});

const NavList = styledComponent(Box)({
  display: "flex",
  flexDirection: "column",
  paddingTop: "8px",
});

const Spacer = styledComponent(Box)({ flex: 1 });

const Footer = styledComponent(Box)({
  display: "flex",
  flexDirection: "column",
  paddingBottom: "16px",
});

const FooterDivider = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  padding: "12px 6px",
  borderTop: "1px solid",
  borderColor: isDark ? "#31363B" : "#D5D9DF",
  gap: "8px",
}));

const ThemePill = TogglePill;
const ThemeOption = ToggleOption;

const ThemeLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: isDark ? "#D5D9DF" : "#31363B",
  whiteSpace: "nowrap",
}));

// ── NavItem ──────────────────────────────────────────────────────────────────

const NavItemRoot = styledComponent(Box)(({ isDark, active }) => ({
  width: "100%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  transition: "all 0.2s",
  margin: "2px 0",
  borderRadius: "8px",
  padding: "8px 12px",
  color: isDark ? (active ? "#F3F6FA" : "#D5D9DF") : "#31363B",
  backgroundColor: active ? (isDark ? "#434950" : "#D5D9DF") : "transparent",
  "& svg": { height: "18px", width: "18px" },
  "&:hover": {
    backgroundColor: isDark ? "#31363B" : "#ECEFF3",
    color: isDark ? "#D5D9DF" : "#31363B",
    "& svg": { color: isDark ? "#D5D9DF" : "#434950" },
  },
}));

const NavItemLabel = styledComponent(Typography)({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

function SidebarItem({ isDark, active, icon, label, onClick }) {
  return (
    <NavItemRoot isDark={isDark} active={active} onClick={onClick}>
      {icon}
      <NavItemLabel>{label}</NavItemLabel>
    </NavItemRoot>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

export default function Sidebar({ open, closeSidebar, openSidebar }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { permissions } = useAuth();

  return (
    <SidebarWrapper>
      <SidebarRoot isDark={isDark} open={open}>
        <Header>
          <LogoImg
            src={isDark ? rwtxLightLogo : rwtxDarkLogo}
            alt='Logo'
            onClick={() => navigate("/")}
          />
          <CloseBtn
            onClick={closeSidebar}
            size='small'
            disableRipple
            isDark={isDark}
          >
            <PanelLeftClose />
          </CloseBtn>
        </Header>

        <NavArea>
          <NavList>
            <SidebarItem
              isDark={isDark}
              active={pathname === "/"}
              icon={<FolderOpen />}
              label='All Files'
              onClick={() => navigate("/")}
            />
            {permissions?.show_logbook_nav && (
              <SidebarItem
                isDark={isDark}
                active={pathname === "/logbook"}
                icon={<ClipboardList />}
                label='Logbook'
                onClick={() => navigate("/logbook")}
              />
            )}
            {permissions?.show_my_procedures && (
              <SidebarItem
                isDark={isDark}
                active={pathname === "/logbook"}
                icon={<ClipboardList />}
                label='My Procedures'
                onClick={() => navigate("/logbook")}
              />
            )}
            {permissions?.show_submissions_nav && (
              <SidebarItem
                isDark={isDark}
                active={pathname === "/submissions"}
                icon={<SubmissionsIcon />}
                label='Submissions'
                onClick={() => navigate("/submissions")}
              />
            )}
            {permissions?.show_settings_nav && (
              <SidebarItem
                isDark={isDark}
                active={pathname === "/admin"}
                icon={<Settings />}
                label='Settings'
                onClick={() => navigate("/admin")}
              />
            )}
          </NavList>

          <Spacer />

          <Footer>
            <FooterDivider isDark={isDark}>
              <ThemePill isDark={isDark}>
                <ThemeOption
                  isDark={isDark}
                  active={!isDark}
                  onClick={() => isDark && toggleTheme()}
                >
                  <Sun />
                </ThemeOption>
                <ThemeOption
                  isDark={isDark}
                  active={isDark}
                  onClick={() => !isDark && toggleTheme()}
                >
                  <Moon />
                </ThemeOption>
              </ThemePill>
              <ThemeLabel isDark={isDark}>
                {isDark ? "Dark Mode" : "Light Mode"}
              </ThemeLabel>
            </FooterDivider>

            <SidebarItem
              isDark={isDark}
              icon={<LogOut />}
              label='Logout'
              onClick={() => navigate("/logout")}
            />
          </Footer>
        </NavArea>
      </SidebarRoot>

      <OpenBtn onClick={openSidebar} disableRipple isDark={isDark} open={open}>
        <PanelLeftOpen />
      </OpenBtn>
    </SidebarWrapper>
  );
}
